import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import type { SubscriptionInfo } from '@/lib/types/subscription'
import type { WorkspaceListItem } from '@/lib/api/features/authentication/types/PostGetCurrentUserResInterface'
import { RXDB_SESSION_STORAGE_KEY } from '@/lib/db/resetUtils'

interface LoginStateValues {
  username: string
  userId: string
  workspaceId: string
  workspaceSlug: string
  workspaceType: number | null
  workspaceKey: string
  workspaceSec: string
  subscriptionInfo: SubscriptionInfo | null
  workspaces: Array<WorkspaceListItem>
  userRole: string
  userPermissions: Array<string>
  // Distinguishes "permissions array hasn't been populated yet" from
  // "permissions array is intentionally empty" (e.g. a custom role with no
  // grants, or an in-flight workspace switch). useHasPermission falls back to
  // the static ROLE_PERMISSIONS map only when this is false — otherwise the
  // live array is authoritative. Without this signal we couldn't tell whether
  // a custom role's empty live array meant "loading" or "deliberately none",
  // which would over-grant via the static map.
  userPermissionsLoaded: boolean
}

interface LoginState extends LoginStateValues {
  setUsername: (uname: string) => void
  setUserId: (id: string) => void
  setWorkspaceId: (workspaceId: string) => void
  setWorkspaceSlug: (workspaceSlug: string) => void
  setWorkspaceType: (workspaceType: number | null) => void
  setWorkspaceKey: (workspaceKey: string) => void
  setWorkspaceSec: (workspaceSec: string) => void
  setSubscriptionInfo: (info: SubscriptionInfo | null) => void
  setWorkspaces: (workspaces: Array<WorkspaceListItem>) => void
  setUserRole: (role: string) => void
  setUserPermissions: (perms: Array<string>) => void
  switchWorkspace: (workspace: WorkspaceListItem) => void
  reset: () => void
}

const initialState: LoginStateValues = {
  username: '',
  userId: '',
  workspaceId: '',
  workspaceSlug: '',
  workspaceType: null,
  workspaceKey: '',
  workspaceSec: '',
  subscriptionInfo: null,
  workspaces: [],
  userRole: 'member',
  userPermissions: [],
  userPermissionsLoaded: false,
}

const useLoginStore = create<LoginState>()(
  devtools(
    persist(
      (set /* _get*/) => ({
        ...initialState,
        setUsername: (uname) => {
          set(() => ({
            username: uname,
          }))
        },
        setUserId: (id) => {
          set(() => ({
            userId: id,
          }))
        },
        setWorkspaceId: (workspaceId) => {
          set(() => ({
            workspaceId: workspaceId,
          }))
        },
        setWorkspaceSlug: (workspaceSlug) => {
          set(() => ({
            workspaceSlug: workspaceSlug,
          }))
        },
        setWorkspaceType: (workspaceType) => {
          set(() => ({
            workspaceType,
          }))
        },
        setWorkspaceKey: (key) => {
          set(() => ({
            workspaceKey: key,
          }))
        },
        setWorkspaceSec: (key) => {
          set(() => ({
            workspaceSec: key,
          }))
        },
        setSubscriptionInfo: (info) => {
          set(() => ({
            subscriptionInfo: info,
          }))
        },
        setWorkspaces: (workspaces) => {
          set(() => ({
            workspaces,
          }))
        },
        setUserRole: (role) => {
          set(() => ({
            userRole: role,
          }))
        },
        setUserPermissions: (perms) => {
          // Stamp the loaded flag the moment the caller (auth bootstrap /
          // workspace switch / refresh) actually populates permissions. Empty
          // array is a valid value — it means "no grants" rather than
          // "still loading", and the loaded flag is what tells the hook to
          // trust the live array over the static role map.
          set(() => ({
            userPermissions: perms,
            userPermissionsLoaded: true,
          }))
        },
        switchWorkspace: (workspace) => {
          // Clear RxDB session flag so splash gate shows for initial sync
          window.sessionStorage.removeItem(RXDB_SESSION_STORAGE_KEY)
          // Clear userPermissions AND the loaded flag — the new workspace
          // hasn't issued its permissions yet, so consumers should fall back
          // to the static role map until setUserPermissions fires for the
          // workspace we just switched to.
          set(() => ({
            workspaceId: workspace.workspaceId,
            workspaceSlug: workspace.workspaceSlug,
            workspaceType: workspace.workspaceType,
            workspaceKey: workspace.workspaceKey,
            workspaceSec: workspace.workspaceSec,
            subscriptionInfo: null,
            userRole: workspace.role ?? 'member',
            userPermissions: [],
            userPermissionsLoaded: false,
          }))
        },
        reset: () => {
          set(() => ({ ...initialState }))
        },
      }),
      {
        name: 'moly',
        storage: createJSONStorage(() => localStorage),
      },
    ),
  ),
)

export default useLoginStore
